import { matchPath } from 'react-router';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import Catalyst from '@Services/Catalyst';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import { getProjectRoutesTypeForAA, getProjectDetail } from '@Pages/RetroSynthesis/constants';
import { RCS10D, RCSANY } from '@Pages/RetroSynthesis/ProjectParametersDialog/constants';

const _getStatus = ({ rtStatus, publishedStatus, rtQueuePosition }) =>
  [
    ...(rtStatus ? [`pairtStatus:${rtStatus}${isNil(rtQueuePosition) ? '' : `:${rtQueuePosition}`}`] : []),
    ...(publishedStatus ? [`publishedStatus:${publishedStatus}`] : []),
  ].join(';');

const _getRouteCount = (count) => (!isNil(count) ? `${count}` : '');

export const onProjectDeleteClick = ({ projects = [] }) => {
  Catalyst.trackEvent('buttonClick', {
    buttonType: 'retrosynthesis:delete:project:start',
    content: projects.map((project) => ({
      id: `project:${project.id}`,
      format: 'project',
      detail: getProjectDetail(project),
      type: getProjectRoutesTypeForAA(project), // e.g.Predicted(20):Published(5):Custom(2)
    })),
  });
};

export const onProjectDeleteConfirm = ({ projects = [] }) => {
  Catalyst.trackEvent('contentDeletion', {
    content: projects.map((project) => ({
      id: `project:${project.id}`,
      format: 'project',
      detail: getProjectDetail(project),
      type: getProjectRoutesTypeForAA(project),
    })),
  });
};

export const onViewResultsClick = (project) => {
  const { id, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount } = project;
  Catalyst.trackEvent('contentSelection', {
    content: [
      {
        id: `project:${id}`,
        format: 'project',
        datapoints: _getRouteCount(rtRouteCount),
        documents: _getRouteCount(publishedRouteCount),
        status: _getStatus({ rtStatus, publishedStatus }),
        detail: getProjectDetail(project),
        type: getProjectRoutesTypeForAA(project), // e.g.Predicted(20):Published(5):Custom(2)
      },
    ],
  });
};

const _getProjectContent = (projectVO) => {
  const { id, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount, rtQueuePosition } = projectVO;
  return {
    id: `project:${id}`,
    format: 'project',
    datapoints: _getRouteCount(rtRouteCount),
    documents: _getRouteCount(publishedRouteCount),
    status: _getStatus({ rtStatus, publishedStatus, rtQueuePosition }),
    detail: getProjectDetail(projectVO),
    type: getProjectRoutesTypeForAA(projectVO),
  };
};

export const onProjectsListLoaded = (projectsList) => {
  if (isEmpty(projectsList)) {
    return;
  }
  const content = projectsList.reduce((acc, projectVO) => {
    acc.push(_getProjectContent(projectVO || {}));
    if (projectVO.childProjects) {
      projectVO.childProjects.forEach((childProject) => {
        acc.push(_getProjectContent(childProject));
      });
    }
    return acc;
  }, []);
  Catalyst.trackEvent('contentWindowLoad', { content });
};

export const onProjectStatusChanged = ({ projectId, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount, position }) => {
  Catalyst.trackEvent('contentWindowLoad', {
    content: [
      {
        id: `project:${projectId}`,
        format: 'project',
        datapoints: _getRouteCount(rtRouteCount),
        documents: _getRouteCount(publishedRouteCount),
        status: _getStatus({ rtStatus, publishedStatus, rtQueuePosition: position }),
      },
    ],
  });
};

export const onProjectExtensionStarted = (childProject) => {
  Catalyst.trackEvent('contentAddition', {
    content: [
      {
        id: `project:${childProject.id}`,
        format: 'project',
        detail: getProjectDetail(childProject),
        type: getProjectRoutesTypeForAA(childProject),
        status: _getStatus(childProject),
      },
    ],
  });
};

export const getOriginPageTypeFromNgRoute = ({ ngRouteName, ngRouteParams = {}, location }) => {
  const { pathname } = location || {};
  switch (ngRouteName) {
    case 'main.search.quick.results': {
      return 'rx2:explicit:preview:autoplan';
    }
    case 'main.results.context':
    case 'main.results.heatmap': {
      return `rx2:explicit:results:${ngRouteParams.context}:autoplan`;
    }
    case 'main.rs': {
      if (matchPath(pathname, { path: RsRoute.DRAW_STRUCTURE, exact: true })) {
        return 'rx2:explicit:draw:synthesisplan';
      }
      return 'rx2:explicit:synthesisplanner:autoplan';
    }
    default: {
      return 'rx2:explicit:undefined:autoplan';
    }
  }
};

export const getSourceKeyHeaderForStatistica = ({ ngRouteName, ngRouteParams = {}, location }) => {
  const { pathname } = location || {};
  switch (ngRouteName) {
    case 'main.results.context': {
      return `main.results.context.${ngRouteParams.context}`;
    }
    case 'main.rs': {
      if (matchPath(pathname, { path: RsRoute.PROJECTS_LIST, exact: true })) {
        return 'main.rs.myprojects';
      }
      if (matchPath(pathname, { path: RsRoute.DRAW_STRUCTURE, exact: true })) {
        return 'main.rs.draw';
      }
      if (matchPath(pathname, { path: RsRoute.PROJECT, exact: true })) {
        return 'main.rs.routes';
      }
      if (matchPath(pathname, { path: RsRoute.PROJECT_ROUTE, exact: true })) {
        return 'main.rs.route';
      }
      if (matchPath(pathname, { path: RsRoute.PROJECT_ROUTE_TREE_VIEW, exact: true })) {
        return 'main.rs.tree';
      }
      return ngRouteName;
    }
    default: {
      return ngRouteName;
    }
  }
};

const generateSearchDataForAA = ({ id, publishedParameters, predictedParameters, editorType, isEditProjectMode, trackingContext }) => {
  let publishedCriteria;
  let predictedCriteria;
  if (publishedParameters) {
    const { defaultYield, maxBranch, maxDepth, numberOfPlans, stopCommand, fullSynthesisPlan } = publishedParameters;

    publishedCriteria = `pu:yes pu-full-routes:${fullSynthesisPlan ? numberOfPlans : 'no'} pu-last-step-only:${
      fullSynthesisPlan ? 'no' : 'yes'
    } pu-branches:${maxBranch} pu-steps:${maxDepth} pu-stop-at-bbs:${stopCommand ? 'yes' : 'no'} pu-yield:${defaultYield}`;
  } else {
    publishedCriteria = 'pu:no';
  }

  if (predictedParameters) {
    const {
      numberOfPlans,
      identicalReactionsSteps,
      identicalBuildingBlocks,
      processingTime,
      fullSynthesisPlan,
      usedBuildingBlocks = [],
      maxNoOfSteps,
      maxPricePerGram,
      partner,
      poolChiral,
      selectivity,
      includeSubstructures = [],
      excludeSubstructures = [],
    } = predictedParameters;

    if (partner === 'IKTOS') {
      let shippingTime = null;
      if (usedBuildingBlocks.indexOf(RCS10D) >= 0) {
        shippingTime = '10';
      }
      if (usedBuildingBlocks.indexOf(RCSANY) >= 0) {
        shippingTime = 'any';
      }
      const customerBuildingBlocks = usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0);
      predictedCriteria =
        // eslint-disable-next-line no-extra-boolean-cast
        `pr:iktos pr-max-steps:${maxNoOfSteps} pr-nostereo:${!!poolChiral ? 'no' : 'yes'} pr-regsel:${selectivity ? 'yes' : 'no'} pr-rcs:${
          shippingTime === null ? 'no' : 'yes'
        } pr-shiptime:${shippingTime} pr-max-price:${maxPricePerGram || 'no'} pr-cust-bb:${
          customerBuildingBlocks.length > 0 ? 'yes' : 'no'
        } pr-time:${processingTime} pr-inter-incl:${includeSubstructures.length} pr-inter-excl:${excludeSubstructures.length}`;
    } else {
      predictedCriteria =
        `pr:pai pr-full-routes:${fullSynthesisPlan ? numberOfPlans : 'no'} pr-last-step-only:${
          fullSynthesisPlan ? 'no' : 'yes'
        } pr-steps:${identicalReactionsSteps} ` +
        `pr-blocks:${identicalBuildingBlocks} pr-time:${processingTime} pr-used-bbs:${usedBuildingBlocks.join(',')}`;
    }
  } else {
    predictedCriteria = 'pr:no';
  }

  const search = {
    criteria: `${editorType} ${predictedCriteria} ${publishedCriteria}`,
    // eslint-disable-next-line no-nested-ternary
    dataFormCriteria: predictedParameters && publishedParameters ? 'both' : predictedParameters ? 'only predicted' : 'only published',
    details: isEditProjectMode ? 'edit' : 'create', // either 'create' when project is new, or 'edit' when existing project is being changed

    // If from reaction results page: 'rx2:explicit:results:reactions:autoplan'
    // If from Substance results page: 'rx2:explicit:results:substances:autoplan'
    // If from Documents results page: 'rx2:explicit:results:citations:autoplan'
    // If from Target results page: 'rx2:explicit:results:tgitems:autoplan'
    // If from Heatmap results page: 'rx2:explicit:results:dpitems:autoplan'
    // If from Syn-Plan (on Reaction results page): 'rx2:explicit:synplan:autoplan'
    // If from Query Preview page: 'rx2:explicit:preview:autoplan'
    // If from Synthesis Planner page: 'rx2:explicit:synthesisplanner:autoplan'
    // If from New Draw page: 'rx2:explicit:draw:synthesisplan'
    ...trackingContext,
  };
  if (id) {
    search.advancedCriteria = `project:${id}`; // evar 19, Advanced Search Criteria
  }
  return search;
};

const getProjectContent = ({ id, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount, rtQueuePosition }) => ({
  id: `project:${id}`,
  format: 'project',
  datapoints: _getRouteCount(rtRouteCount),
  documents: _getRouteCount(publishedRouteCount),
  status: _getStatus({ rtStatus, publishedStatus, rtQueuePosition }),
  detail: `project:${id}`,
  type: getProjectRoutesTypeForAA({ rtRouteCount, publishedRouteCount }),
});

export const onPageLoad = (createdProject) => {
  let search; // Only define if a project was just created or edited causing this page load
  let content;
  if (createdProject) {
    const {
      id,
      publishedParameters,
      predictedParameters,
      editorType,
      isEditProjectMode,
      publishedStatus,
      publishedRouteCount,
      rtStatus,
      rtRouteCount,
      trackingContext,
      breakBondCount,
      protectBondCount,
      rtQueuePosition,
    } = createdProject;

    search = generateSearchDataForAA({
      id,
      publishedParameters,
      predictedParameters,
      editorType,
      isEditProjectMode,
      trackingContext,
    });

    content = [
      getProjectContent({
        id,
        publishedStatus,
        publishedRouteCount,
        rtStatus,
        rtRouteCount,
        rtQueuePosition,
      }),
    ];

    if (breakBondCount) {
      Catalyst.trackEvent('productFeatureUsed', {
        content,
        feature: {
          name: `break bond:${breakBondCount}`, // break bond:<number of broken bonds>
        },
      });
    }

    if (protectBondCount) {
      Catalyst.trackEvent('productFeatureUsed', {
        content,
        feature: {
          name: `protect bond:${protectBondCount}`, // protect  bond:<number of protect bonds>
        },
      });
    }
  }

  Catalyst.trackEvent('newPage', {
    page: {
      name: 'retrosynthesis:myprojects',
    },
    search,
    content,
  });
};

export const onImportProjects = (importedProjects) => {
  const { publishedParameters, predictedParameters } = importedProjects[0];

  const search = generateSearchDataForAA({
    publishedParameters,
    predictedParameters,
    editorType: 'import',
    isEditProjectMode: false,
    trackingContext: {
      type: 'rx2:explicit:import:synthesisplan',
    },
  });

  const content = importedProjects.map(
    ({ id, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount, breakBondCount, protectBondCount, rtQueuePosition }) =>
      getProjectContent({
        id,
        publishedStatus,
        publishedRouteCount,
        rtStatus,
        rtRouteCount,
        breakBondCount,
        protectBondCount,
        rtQueuePosition,
      })
  );

  Catalyst.trackEvent('searchResultsUpdated', {
    search,
    content,
  });
};

export const onSimilarSubstanceSubmit = ({ id, publishedStatus, publishedRouteCount, rtStatus, rtRouteCount }) => {
  Catalyst.trackEvent('searchWithinContent', {
    content: [
      {
        id: `project:${id}`,
        format: 'project',
        datapoints: _getRouteCount(rtRouteCount), // '<NUMBER OF PREDICTED RESULTS>'
        documents: _getRouteCount(publishedRouteCount), // '<NUMBER OF PUBLISHED RESULTS>'
        status: _getStatus({ rtStatus, publishedStatus }),
      },
    ],
  });
};

export const onProjectNameEdit = ({ action, trackingContent }) => {
  Catalyst.trackEvent('productFeatureUsed', {
    feature: {
      name: `retrosynthesis:edit project name:${action}`, // prop25
    },
    content: trackingContent,
  });
};

export const onProjectParametersClick = (open) => {
  Catalyst.trackEvent('buttonClick', {
    buttonType: `chevron:${open ? 'show' : 'close'}-parameters`, // v124
  });
};

export const onGroupExpandCollapse = (isExpand, projects) => {
  const content = projects.map((project) => ({
    id: `project:${project.id}`,
    format: 'project',
    detail: getProjectDetail(project),
    type: getProjectRoutesTypeForAA(project),
  }));

  Catalyst.trackEvent('buttonClick', {
    buttonType: `chevron:${isExpand ? 'expand' : 'collapse'}`, // v124
    content,
  });
};

export const onProjectEdit = (project) => {
  Catalyst.trackEvent('navigationClick', {
    content: [
      {
        id: `project:${project.id}`,
        format: 'project',
        detail: getProjectDetail(project),
        type: getProjectRoutesTypeForAA(project),
      },
    ],
    link: {
      name: 'Edit',
    },
  });
};
